<!--
  ConcussionAssessment — Admin-only concussion / head-injury record.

  IMPORTANT (scope + rationale):
  The SCAT6 is BMJ copyright; "digital reformatting" of it is not permitted
  without BMJ's written consent, and it is a Health-Care-Professional-only tool.
  So this component does NOT reproduce the scored SCAT6 form. Instead it records
  the WORKFLOW and OUTCOME around a suspected concussion (red flags, removal from
  play, referral, diagnosis, graduated return-to-play) and lets staff attach the
  COMPLETED OFFICIAL SCAT6/CRT6 PDF — the sanctioned way to use the tool.

  Non-clinician admins should use the CRT6 (Concussion Recognition Tool 6); the
  SCAT6 is for medical staff. SCAT6 is for ages 13+ (Child SCAT6 for 12 & under).
-->
<template>
  <div class="concussion-assessment">
    <div class="ca-intro">
      <div>
        <h3 class="ca-title">Concussion assessments</h3>
        <p class="ca-sub">
          Record a suspected concussion / head injury and attach the completed
          official tool. Non-clinicians should use the
          <a :href="CRT6_URL" target="_blank" rel="noopener">CRT6</a>; the
          <a :href="SCAT6_URL" target="_blank" rel="noopener">SCAT6</a> is for
          medical staff (ages 13+).
        </p>
      </div>
      <button v-if="isAdmin" class="green-button" @click="openNew">
        <i class="bi bi-plus-lg"></i> New assessment
      </button>
    </div>

    <!-- Existing records -->
    <div v-if="loading" class="ca-empty">Loading…</div>
    <div v-else-if="!assessments.length" class="ca-empty">
      No concussion assessments recorded for this player.
    </div>
    <table v-else class="ca-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Red flags</th>
          <th>Removed</th>
          <th>Diagnosed</th>
          <th>Return to play</th>
          <th>Tool</th>
          <th>PDF</th>
          <th v-if="isAdmin"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="a in assessments" :key="a.id">
          <td>{{ formatDate(a.assessment_date) }}</td>
          <td>
            <span v-if="(a.red_flags || []).length" class="ca-badge ca-badge--danger">
              {{ a.red_flags.length }} flag{{ a.red_flags.length > 1 ? "s" : "" }}
            </span>
            <span v-else class="ca-muted">—</span>
          </td>
          <td>{{ a.removed_from_play == 1 ? "Yes" : "No" }}</td>
          <td>
            <span :class="diagnosedClass(a.concussion_diagnosed)">{{
              a.concussion_diagnosed || "—"
            }}</span>
          </td>
          <td>{{ a.return_to_play_stage || "—" }}</td>
          <td>{{ a.tool_used || "—" }}</td>
          <td>
            <a v-if="a.document_path" :href="fileUrl(a.document_path)" target="_blank" rel="noopener">
              <i class="bi bi-file-earmark-pdf"></i>
            </a>
            <span v-else class="ca-muted">—</span>
          </td>
          <td v-if="isAdmin" class="ca-actions">
            <button class="ca-icon-btn" title="Edit" @click="openEdit(a)">
              <i class="bi bi-pencil"></i>
            </button>
            <button class="ca-icon-btn ca-icon-btn--danger" title="Delete" @click="remove(a)">
              <i class="bi bi-trash"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Create / edit modal -->
    <div v-if="showModal" class="ca-overlay" @click.self="closeModal">
      <div class="ca-dialog">
        <div class="ca-dialog__header">
          <h3>{{ form.id ? "Edit" : "New" }} concussion assessment</h3>
          <button class="ca-close" @click="closeModal">&times;</button>
        </div>

        <div class="ca-dialog__body">
          <!-- Urgent red-flag warning -->
          <div v-if="hasRedFlags" class="ca-alert ca-alert--danger">
            <strong>Red flag(s) present — urgent medical attention required.</strong>
            If a qualified medical practitioner is not available for immediate
            assessment, activate emergency procedures and arrange urgent transport
            to the nearest hospital. Remove the player from play immediately.
          </div>

          <div class="ca-grid">
            <label class="ca-field">
              <span>Assessment date</span>
              <input type="date" v-model="form.assessment_date" />
            </label>
            <label class="ca-field">
              <span>Time of injury</span>
              <input type="time" v-model="form.time_of_injury" />
            </label>
          </div>

          <label class="ca-field">
            <span>Mechanism / what happened</span>
            <textarea v-model="form.mechanism" rows="2"
              placeholder="e.g. head-to-head clash, fall to ground…"></textarea>
          </label>

          <!-- Red flags (Box 1) -->
          <fieldset class="ca-fieldset">
            <legend>Red flags <span class="ca-muted">(tick any present)</span></legend>
            <div class="ca-checks">
              <label v-for="flag in RED_FLAGS" :key="flag" class="ca-check">
                <input type="checkbox" :value="flag" v-model="form.red_flags" />
                <span>{{ flag }}</span>
              </label>
            </div>
          </fieldset>

          <!-- On-field observable signs -->
          <fieldset class="ca-fieldset">
            <legend>On-field observable signs</legend>
            <div class="ca-checks">
              <label v-for="s in OBSERVABLE_SIGNS" :key="s" class="ca-check">
                <input type="checkbox" :value="s" v-model="form.observable_signs" />
                <span>{{ s }}</span>
              </label>
            </div>
          </fieldset>

          <!-- Reported symptoms -->
          <fieldset class="ca-fieldset">
            <legend>Reported symptoms <span class="ca-muted">(tick any present)</span></legend>
            <div class="ca-checks">
              <label v-for="s in SYMPTOMS" :key="s" class="ca-check">
                <input type="checkbox" :value="s" v-model="form.symptoms" />
                <span>{{ s }}</span>
              </label>
            </div>
          </fieldset>

          <!-- Quick clinical screen -->
          <div class="ca-grid ca-grid--3">
            <label class="ca-field">
              <span>Oriented (time/place/person)</span>
              <select v-model="form.details.orientation">
                <option value="">—</option>
                <option>Yes</option>
                <option>Partial</option>
                <option>No</option>
              </select>
            </label>
            <label class="ca-field">
              <span>Glasgow Coma Scale (3–15)</span>
              <input type="number" min="3" max="15" v-model="form.details.gcs_total" />
            </label>
            <label class="ca-field">
              <span>Balance / coordination</span>
              <select v-model="form.details.balance">
                <option value="">—</option>
                <option>Normal</option>
                <option>Abnormal</option>
                <option>Not tested</option>
              </select>
            </label>
          </div>
          <p
            v-if="form.details.gcs_total !== '' && Number(form.details.gcs_total) < 15"
            class="ca-note ca-note--warn"
          >
            A Glasgow Coma Scale below 15 is a red flag — urgent medical attention required.
          </p>

          <label class="ca-field">
            <span>Cognitive / other screen notes</span>
            <textarea v-model="form.details.cognitive_notes" rows="2"></textarea>
          </label>

          <div class="ca-grid">
            <label class="ca-field">
              <span>Assessed by</span>
              <input type="text" v-model="form.assessed_by" placeholder="Name" />
            </label>
            <label class="ca-field ca-field--check">
              <input type="checkbox" v-model="form.assessed_by_is_hcp" />
              <span>Assessor is a Health Care Professional (HCP)</span>
            </label>
          </div>

          <div class="ca-grid ca-grid--3">
            <label class="ca-field">
              <span>Tool used</span>
              <select v-model="form.tool_used">
                <option value="">—</option>
                <option>CRT6</option>
                <option>SCAT6</option>
                <option>Other</option>
                <option>None</option>
              </select>
            </label>
            <label class="ca-field">
              <span>Referred to</span>
              <select v-model="form.referred_to">
                <option value="">—</option>
                <option>None</option>
                <option>GP/HCP</option>
                <option>Hospital/A&E</option>
              </select>
            </label>
            <label class="ca-field">
              <span>Concussion diagnosed</span>
              <select v-model="form.concussion_diagnosed">
                <option value="">—</option>
                <option>Deferred</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </label>
          </div>

          <div class="ca-grid">
            <label class="ca-field ca-field--check">
              <input type="checkbox" v-model="form.removed_from_play" />
              <span>Removed from play</span>
            </label>
            <label class="ca-field">
              <span>Return-to-play stage</span>
              <select v-model="form.return_to_play_stage">
                <option value="">Not started</option>
                <option v-for="s in RTP_STAGES" :key="s" :value="s">{{ s }}</option>
              </select>
            </label>
          </div>

          <p v-if="!form.removed_from_play" class="ca-note ca-note--warn">
            An athlete with a suspected concussion must be removed from play and
            must not return to play on the day of injury.
          </p>

          <!-- Optionally attach the completed official PDF as well -->
          <label class="ca-field">
            <span>Attach completed official SCAT6 / CRT6 (PDF, optional)</span>
            <input ref="fileInput" type="file" accept="application/pdf" @change="onFile" />
            <span v-if="uploading" class="ca-muted">Uploading…</span>
            <span v-else-if="form.document_name" class="ca-muted">
              Attached: {{ form.document_name }}
            </span>
          </label>

          <label class="ca-field">
            <span>Notes</span>
            <textarea v-model="form.notes" rows="3"></textarea>
          </label>

          <label class="ca-field">
            <span>Status</span>
            <select v-model="form.status">
              <option>Open</option>
              <option>Resolved</option>
            </select>
          </label>
        </div>

        <div class="ca-dialog__footer">
          <button class="ca-btn-secondary" @click="closeModal">Cancel</button>
          <button class="green-button" :disabled="saving || uploading" @click="save">
            {{ saving ? "Saving…" : "Save assessment" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Axios from "axios";
import { PlayerInjury } from "@/services/player-Injury-service.js";

// Official tool references (BMJ, Br J Sports Med 2023;57:11). Confirm/update if needed.
const SCAT6_URL = "https://bjsm.bmj.com/content/57/11/622";
const CRT6_URL = "https://bjsm.bmj.com/content/57/11/692";

// Box 1 red flags (verbatim from SCAT6 — factual safety list).
const RED_FLAGS = [
  "Neck pain or tenderness",
  "Seizure or convulsion",
  "Double vision",
  "Loss of consciousness",
  "Weakness or tingling/burning in more than 1 arm or in the legs",
  "Deteriorating conscious state",
  "Vomiting",
  "Severe or increasing headache",
  "Increasingly restless, agitated or combative",
  "GCS <15",
  "Visible deformity of the skull",
];

// Graduated return-to-play stages (CISG consensus — generic clinical sequence).
const RTP_STAGES = [
  "1. Symptom-limited activity",
  "2. Light aerobic exercise",
  "3. Sport-specific exercise",
  "4. Non-contact training drills",
  "5. Full-contact practice (after medical clearance)",
  "6. Return to play",
];

// On-field observable signs (general observational cues — our own wording).
const OBSERVABLE_SIGNS = [
  "Lying motionless",
  "Slow to get up",
  "Unsteady on feet / balance problems",
  "Disorientation or confusion",
  "Blank or vacant look",
  "Facial injury after head trauma",
  "Loss of responsiveness",
];

// Common concussion symptoms (present/absent checklist — our own simplified list,
// NOT the SCAT6 scored 0–6 scale).
const SYMPTOMS = [
  "Headache",
  "Pressure in head",
  "Neck pain",
  "Nausea",
  "Dizziness",
  "Blurred vision",
  "Sensitivity to light",
  "Sensitivity to noise",
  "Balance problems",
  "Feeling slowed down",
  "Feeling in a fog",
  "Difficulty concentrating",
  "Difficulty remembering",
  "Fatigue / low energy",
  "Confusion",
  "Drowsiness",
  "More emotional / irritable",
];

function emptyForm() {
  return {
    id: null,
    assessment_date: new Date().toISOString().split("T")[0],
    time_of_injury: "",
    mechanism: "",
    red_flags: [],
    observable_signs: [],
    symptoms: [],
    details: {
      orientation: "",
      gcs_total: "",
      balance: "",
      cognitive_notes: "",
    },
    assessed_by: "",
    assessed_by_is_hcp: false,
    tool_used: "",
    removed_from_play: true,
    referred_to: "",
    concussion_diagnosed: "",
    return_to_play_stage: "",
    document_path: "",
    document_name: "",
    notes: "",
    status: "Open",
  };
}

export default {
  name: "ConcussionAssessment",
  props: {
    playerId: { type: [Number, String], required: true },
    clubId: { type: [Number, String], default: null },
    userData: { type: Object, required: true },
  },
  data() {
    return {
      SCAT6_URL,
      CRT6_URL,
      RED_FLAGS,
      RTP_STAGES,
      OBSERVABLE_SIGNS,
      SYMPTOMS,
      assessments: [],
      loading: false,
      showModal: false,
      saving: false,
      uploading: false,
      form: emptyForm(),
    };
  },
  computed: {
    isAdmin() {
      return this.userData && this.userData.userType === "Admin";
    },
    hasRedFlags() {
      return this.form.red_flags && this.form.red_flags.length > 0;
    },
  },
  watch: {
    playerId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.playerId) return;
      this.loading = true;
      try {
        const res = await PlayerInjury.find({
          action: "getConcussionAssessment",
          player_id: this.playerId,
        });
        this.assessments = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Failed to load concussion assessments", e);
      } finally {
        this.loading = false;
      }
    },

    openNew() {
      this.form = emptyForm();
      this.showModal = true;
    },
    openEdit(a) {
      this.form = {
        ...emptyForm(),
        ...a,
        red_flags: Array.isArray(a.red_flags) ? [...a.red_flags] : [],
        observable_signs: Array.isArray(a.observable_signs) ? [...a.observable_signs] : [],
        symptoms: Array.isArray(a.symptoms) ? [...a.symptoms] : [],
        details:
          a.assessment_details && typeof a.assessment_details === "object"
            ? { ...emptyForm().details, ...a.assessment_details }
            : emptyForm().details,
        assessed_by_is_hcp: a.assessed_by_is_hcp == 1,
        removed_from_play: a.removed_from_play == 1,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },

    onFile(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.uploadPdf(file);
    },
    async uploadPdf(file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "document");
      const uploadUrl =
        window.location.href.indexOf("8080") > -1
          ? "http://www.localhost:8888/back-end-endpoint-chat-upload.php"
          : "/personal-training-BE/back-end-endpoint-chat-upload.php";
      this.uploading = true;
      try {
        const res = await Axios.post(uploadUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data && res.data.success && res.data.file) {
          this.form.document_path = res.data.file.path;
          this.form.document_name = res.data.file.original_name || file.name;
        } else {
          throw new Error((res.data && res.data.error) || "Upload failed");
        }
      } catch (e) {
        console.error("PDF upload failed", e);
        this.$toast && this.$toast.error
          ? this.$toast.error("Could not upload the PDF.")
          : alert("Could not upload the PDF.");
      } finally {
        this.uploading = false;
      }
    },

    async save() {
      if (!this.form.assessment_date) {
        alert("Please set an assessment date.");
        return;
      }
      this.saving = true;
      const payload = {
        ...this.form,
        assessment_details: this.form.details,
        action: this.form.id ? "updateConcussionAssessment" : "saveConcussionAssessment",
        player_id: this.playerId,
        club_id: this.clubId,
      };
      try {
        await PlayerInjury.find(payload);
        this.$toast && this.$toast.success && this.$toast.success("Assessment saved");
        this.showModal = false;
        await this.load();
      } catch (e) {
        console.error("Failed to save concussion assessment", e);
        this.$toast && this.$toast.error
          ? this.$toast.error("Could not save the assessment.")
          : alert("Could not save the assessment.");
      } finally {
        this.saving = false;
      }
    },

    async remove(a) {
      if (!confirm("Delete this concussion assessment?")) return;
      try {
        await PlayerInjury.find({
          action: "deleteConcussionAssessment",
          id: a.id,
        });
        await this.load();
      } catch (e) {
        console.error("Failed to delete concussion assessment", e);
      }
    },

    formatDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      return isNaN(dt) ? d : dt.toLocaleDateString("en-GB");
    },
    fileUrl(path) {
      if (!path) return "#";
      if (path.startsWith("http")) return path;
      return "/" + path.replace(/^\//, "");
    },
    diagnosedClass(v) {
      if (v === "Yes") return "ca-badge ca-badge--danger";
      if (v === "No") return "ca-badge ca-badge--ok";
      if (v === "Deferred") return "ca-badge ca-badge--warn";
      return "ca-muted";
    },
  },
};
</script>

<style scoped lang="scss">
.concussion-assessment {
  width: 100%;
}
.ca-intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 1rem;
}
.ca-title {
  margin: 0 0 4px;
}
.ca-sub {
  margin: 0;
  font-size: 0.9rem;
  color: #65676b;
  max-width: 640px;
}
.ca-sub a {
  color: var(--brand-color, #1e87f0);
}
.ca-empty {
  padding: 1.5rem;
  color: #65676b;
  background: #f7f8fa;
  border-radius: 10px;
}
.ca-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.ca-table th,
.ca-table td {
  text-align: left;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #eee;
}
.ca-table th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8a8f98;
}
.ca-muted {
  color: #adb5bd;
}
.ca-actions {
  white-space: nowrap;
  text-align: right;
}
.ca-icon-btn {
  border: none;
  background: #f0f2f5;
  border-radius: 8px;
  padding: 6px 8px;
  margin-left: 4px;
  cursor: pointer;
  color: #555;
}
.ca-icon-btn--danger:hover {
  background: #fce8e6;
  color: #d93025;
}
.ca-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}
.ca-badge--danger {
  background: #fce8e6;
  color: #c5221f;
}
.ca-badge--warn {
  background: #fef7e0;
  color: #b06000;
}
.ca-badge--ok {
  background: #e6f4ea;
  color: #137333;
}

/* Modal */
.ca-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 4vh 16px;
  overflow-y: auto;
}
.ca-dialog {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 640px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}
.ca-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}
.ca-dialog__header h3 {
  margin: 0;
  font-size: 1.15rem;
}
.ca-close {
  border: none;
  background: none;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  color: #888;
}
.ca-dialog__body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
}
.ca-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #eee;
}
.ca-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.ca-grid--3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.ca-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.9rem;
}
.ca-field > span {
  font-weight: 600;
  color: #444;
}
.ca-field input[type="text"],
.ca-field input[type="date"],
.ca-field input[type="time"],
.ca-field input[type="number"],
.ca-field select,
.ca-field textarea {
  border: 1px solid #d5d9de;
  border-radius: 8px;
  padding: 0.5rem 0.6rem;
  font: inherit;
}
/* checkbox rows — reset global `label { display:block }` bleed-through */
.ca-field--check {
  display: flex !important;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  text-align: left;
}
.ca-field--check > span {
  font-weight: 600;
  color: #444;
}
.ca-field--check input[type="checkbox"] {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin: 0;
}
.ca-fieldset {
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}
.ca-fieldset legend {
  font-size: 0.85rem;
  font-weight: 700;
  padding: 0 6px;
  width: auto;
}
.ca-checks {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 20px;
  justify-items: start;
}
.ca-check {
  display: flex !important;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  margin: 0;
  text-align: left;
  font-size: 0.85rem;
  font-weight: 400;
  color: #333;
}
.ca-check input[type="checkbox"] {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin: 0;
}
.ca-check span {
  flex: 1 1 auto;
  text-align: left;
}
.ca-alert {
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 0.9rem;
  line-height: 1.4;
}
.ca-alert--danger {
  background: #fce8e6;
  border: 1px solid #f5c2bd;
  color: #b3261e;
}
.ca-note {
  margin: 0;
  font-size: 0.85rem;
}
.ca-note--warn {
  color: #b06000;
}
.ca-btn-secondary {
  border: 1px solid #d5d9de;
  background: #fff;
  color: #333; /* override global button{color:white} */
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  min-width: 90px;
  font-weight: 500;
  cursor: pointer;
}
.ca-btn-secondary:hover,
.ca-btn-secondary:focus {
  background: #f0f2f5;
}
@media (max-width: 768px) {
  .ca-grid,
  .ca-grid--3,
  .ca-checks {
    grid-template-columns: 1fr;
  }
}
</style>
